import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, Check, Lock, CheckCircle2, AlertTriangle, XCircle, Clock } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { TrustBreakdownCard } from "@/components/TrustBreakdownCard";

const TYPE_META = {
    identity: { label: "Identity", detail: "Government-issued ID + selfie match." },
    employment: { label: "Employment", detail: "Past employer confirmation of role and dates." },
    education: { label: "Education", detail: "Degree, institution, dates of attendance." },
    reference: { label: "References", detail: "Structured reference checks with 2–3 contacts." },
    // NOTE: 'background' was intentionally removed from the talent-side
    // consent center in Feb 2026. Employer-initiated employment background
    // screenings live under their own hiring-stage workflow (screening_requests)
    // and are never reusable across employers.
};

const AUTHORIZATION_TEXT =
    "I authorize Simonara Talent Solutions and its verification partners to verify the information I voluntarily submit.";

// Human-friendly rendering for the outcome the ops team recorded (if any).
// Talents NEVER see internal states like "pending" / "running" — those roll
// up into "In review".
const OUTCOME_META = {
    verified:        { icon: CheckCircle2,  label: "Verified",     tone: "text-emerald-700 dark:text-emerald-400", detail: "Confirmed by our verification team." },
    in_progress:     { icon: Clock,         label: "In progress",  tone: "text-sky-700 dark:text-sky-400",         detail: "Our team is running your check now." },
    review_required: { icon: AlertTriangle, label: "Under review", tone: "text-amber-700 dark:text-amber-300",     detail: "We're taking another look. We'll follow up if we need anything." },
    unsuccessful:    { icon: XCircle,       label: "Not verified", tone: "text-rose-700 dark:text-rose-400",       detail: "Couldn't be confirmed. See email for details or contact support." },
    in_review:       { icon: Clock,         label: "In review",    tone: "text-slate-600 dark:text-slate-300",     detail: "Waiting for our verification team." },
};

function _talentOutcomeKey(rec) {
    // ``rec`` is ``outcomes_by_type[type]`` — { outcome, status, completed_at }
    if (!rec) return null;
    if (rec.outcome && OUTCOME_META[rec.outcome]) return rec.outcome;
    if (rec.status === "in_progress") return "in_progress";
    // Legacy statuses roll up to "in_review" (never surface the internal words).
    if (rec.status && ["pending", "running"].includes(rec.status)) return "in_review";
    if (rec.status === "completed") return "verified";
    if (rec.status === "failed") return "unsuccessful";
    return "in_review";
}

export default function VerifyConsentCenter() {
    const { user } = useAuth();
    const [consents, setConsents] = useState({}); // latest_by_type from server
    const [outcomes, setOutcomes] = useState({}); // outcomes_by_type from server
    const [checked, setChecked] = useState({});   // local ticked-but-not-submitted
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState({});

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/verify/consent");
            setConsents(data.latest_by_type || {});
            setOutcomes(data.outcomes_by_type || {});
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const submit = async (type) => {
        if (!checked[type]) {
            toast.error("Please tick the authorization box first.");
            return;
        }
        try {
            setSubmitting((s) => ({ ...s, [type]: true }));
            const { data } = await api.post("/verify/consent", { verification_type: type, consented: true });
            setConsents((c) => ({ ...c, [type]: data }));
            toast.success(`${TYPE_META[type].label} consent recorded.`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting((s) => ({ ...s, [type]: false }));
        }
    };

    const summary = useMemo(() => {
        const total = Object.keys(TYPE_META).length;
        const granted = Object.keys(TYPE_META).filter((t) => consents[t]).length;
        return { granted, total };
    }, [consents]);

    return (
        <div data-testid="verify-consent-page">
            <div className="mb-6">
                <p className="overline">Simonara Verify™</p>
                <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    My Verifications
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    You control which categories employers can verify. Grant consent per type — no verification runs without your explicit authorization. Every consent is signed, timestamped, IP-logged, and downloadable as a PDF from below.
                </p>
            </div>

            {/* Trust breakdown card */}
            {user?.email && (
                <div className="mb-4">
                    <TrustBreakdownCard email={user.email} />
                </div>
            )}

            <div className="card-soft p-4 mb-4 flex items-start gap-2 bg-emerald-500/5 border-emerald-500/20">
                <Lock size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground">
                    <b className="text-foreground">You&apos;re in control.</b> Consent is stored with a timestamp and the exact authorization language.
                    You&apos;ve granted <b className="text-foreground">{summary.granted}</b> of <b className="text-foreground">{summary.total}</b> possible categories.
                </p>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(TYPE_META).map(([type, meta]) => {
                        const granted = consents[type];
                        return (
                            <motion.div
                                key={type}
                                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                                className={`card-soft p-5 ${granted ? "border-emerald-500/30" : ""}`}
                                data-testid={`consent-card-${type}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <h3 className="font-heading font-semibold text-foreground">{meta.label}</h3>
                                        <p className="text-xs text-muted-foreground mt-0.5">{meta.detail}</p>
                                    </div>
                                    <div className={`shrink-0 rounded-full p-1.5 border ${granted ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" : "border-border text-muted-foreground"}`}>
                                        <ShieldCheck size={14} />
                                    </div>
                                </div>
                                {granted ? (
                                    <div className="mt-4 space-y-2" data-testid={`consent-granted-${type}`}>
                                        <div className="flex flex-wrap items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400">
                                            <Check size={12} /> Consent granted on {new Date(granted.consented_at).toLocaleDateString()}
                                            <a
                                                href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/verify/consents/${granted.id}/record`}
                                                target="_blank"
                                                rel="noreferrer noopener"
                                                data-testid={`consent-download-${type}`}
                                                className="ml-1 inline-flex items-center gap-1 rounded-full border border-emerald-200 dark:border-emerald-500/40 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-500/10"
                                            >
                                                Download record (PDF)
                                            </a>
                                        </div>
                                        {(() => {
                                            const oKey = _talentOutcomeKey(outcomes[type]) || "in_review";
                                            const oMeta = OUTCOME_META[oKey];
                                            const OIcon = oMeta.icon;
                                            return (
                                                <div
                                                    data-testid={`consent-outcome-${type}`}
                                                    className={`text-xs inline-flex items-start gap-1.5 rounded-lg border border-border/80 bg-muted/40 px-2.5 py-1.5 ${oMeta.tone}`}
                                                >
                                                    <OIcon size={13} className="mt-0.5 shrink-0" />
                                                    <span>
                                                        <span className="font-semibold">{oMeta.label}</span>
                                                        <span className="text-muted-foreground ml-1">— {oMeta.detail}</span>
                                                    </span>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                ) : (
                                    <div className="mt-4">
                                        <label className="flex items-start gap-2 rounded-lg border border-dashed border-border p-3 cursor-pointer hover:border-emerald-500/40 transition-colors">
                                            <Checkbox
                                                checked={!!checked[type]}
                                                onCheckedChange={(v) => setChecked((c) => ({ ...c, [type]: !!v }))}
                                                className="mt-0.5"
                                                data-testid={`consent-check-${type}`}
                                            />
                                            <span className="text-xs text-foreground italic">&ldquo;{AUTHORIZATION_TEXT}&rdquo;</span>
                                        </label>
                                        <button
                                            onClick={() => submit(type)}
                                            disabled={!checked[type] || submitting[type]}
                                            className="btn-green mt-3 w-full inline-flex items-center justify-center gap-1 text-sm disabled:opacity-60"
                                            data-testid={`consent-submit-${type}`}
                                        >
                                            {submitting[type] ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />} Grant {meta.label} consent
                                        </button>
                                    </div>
                                )}
                            </motion.div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
